import React, { useEffect, useState } from "react";
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import PathPickStartGuideOfficialGuideLink from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideOfficialGuideLink";
import useGetSmartStoreInfo from "@/features/pathpick/features/basket/hooks/query/useGetSmartStoreInfo";
import useCreateSmartStoreInfo from "@/features/pathpick/features/basket/hooks/mutation/useCreateSmartStoreInfo";
import useUpdateSmartStoreInfo from "@/features/pathpick/features/basket/hooks/mutation/useUpdateSmartStoreInfo";
import { toPathPickSmartStoreInfoRequestBody } from "@/features/pathpick/features/basket/utils";
import { SMARTSTORE_DELIVERY_TEMPLATE_GUIDE_URL } from "@/features/pathpick/features/basket/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isCompleted: boolean;
  onSave: () => void;
}

/**
 * 배송비 템플릿 코드 입력 — 엑셀 방식에서만 나온다.
 *
 * API 방식에서는 절대 노출하지 않는다(전달서 §8.5). 이 분기는 `useStartGuideReadiness` 가 쥔다.
 * 가이드 안이지만 값은 목이 아니라 실제 스마트스토어 기본 정보에 저장한다 —
 * 여기서 넣은 코드가 담은 상품의 파일 신청에서 그대로 쓰여야 하기 때문이다.
 * 저장해도 모달을 닫지 않는다 — 저장된 코드를 그 자리에서 확인할 수 있어야 하기 때문이다.
 */
function PathPickStartGuideDeliveryTemplateModal({
  isOpen,
  onClose,
  isCompleted,
  onSave,
}: Props) {
  const { t } = useI18nHelper();
  const {
    smartStoreInfo,
    isError: isSmartStoreInfoError,
    isFetching: isSmartStoreInfoFetching,
    refetch: refetchSmartStoreInfo,
  } = useGetSmartStoreInfo();
  const { mutate: createSmartStoreInfo, isPending: isCreating } =
    useCreateSmartStoreInfo();
  const { mutate: updateSmartStoreInfo, isPending: isUpdating } =
    useUpdateSmartStoreInfo();

  const [code, setCode] = useState("");
  const [hasError, setHasError] = useState(false);

  const savedCode = smartStoreInfo?.deliveryTemplateCode ?? "";
  const isSaving = isCreating || isUpdating;

  // 저장 여부를 아예 모르는 상태 — 한 번도 못 읽었을 때만이다.
  // (404는 훅에서 null(미설정)로 흡수하므로 여기 걸리지 않는다)
  // 이 상태에서 저장하면 등록/수정 분기가 뒤집혀(POST) 이미 저장된 A/S·배송 정보가
  // 빈 값으로 덮인다 — 읽기 전에는 쓰지 않는다.
  const isSmartStoreInfoUnavailable =
    isSmartStoreInfoError && smartStoreInfo === undefined;

  // 다시 열 때는 저장돼 있던 값에서 시작한다(직전에 취소한 편집은 버린다).
  useEffect(() => {
    if (isOpen) {
      setCode(savedCode);
      setHasError(false);
    }
  }, [isOpen, savedCode]);

  const trimmed = code.trim();
  // 저장한 값을 그대로 두고 있을 때만 "저장 완료"다 — 다른 코드를 치기 시작하면 다시 저장해야 한다.
  const isSaved = isCompleted && trimmed === savedCode && savedCode.length > 0;

  const handleSave = () => {
    // 미리 막지 않고 눌렀을 때 알려 준다 — 왜 못 누르는지 알 수 없는 상태를 만들지 않는다.
    if (trimmed.length === 0) {
      setHasError(true);
      return;
    }
    if (isSmartStoreInfoUnavailable) {
      return;
    }
    setCode(trimmed);

    // 저장된 정보가 있으면 수정(PUT), 없으면 등록(POST). 실패 토스트는 훅의 onError가 담당.
    // 수정은 전체 PUT이라 템플릿 코드만 보내면 나머지가 빈 값으로 덮인다 —
    // 저장된 값을 그대로 되돌려 보내고 코드만 바꾼다.
    const save = smartStoreInfo ? updateSmartStoreInfo : createSmartStoreInfo;
    save(
      {
        ...toPathPickSmartStoreInfoRequestBody(smartStoreInfo),
        deliveryTemplateCode: trimmed,
      },
      { onSuccess: () => onSave() }
    );
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"560px"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick_guide:delivery_template.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <PathPickStartGuideMenuPath
            path={t("path_pick_guide:delivery_template.menu_path")}
          />
          <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
            {t("path_pick_guide:delivery_template.description")}
          </Text>

          {/* 저장된 기본 정보를 못 읽었을 때 — 저장 버튼이 잠기는 이유를 먼저 알린다.
              문구는 기본 정보 설정 모달과 같은 것을 쓴다(같은 조회, 같은 원인). */}
          {isSmartStoreInfoUnavailable && (
            <PathPickInlineNotice
              tone={"red"}
              title={t("path_pick:basket.buyer_info.load_error.title")}
              action={
                <AppButton
                  variant={"white"}
                  size={"small"}
                  isLoading={isSmartStoreInfoFetching}
                  onClick={() => refetchSmartStoreInfo()}
                >
                  <Text textStyle={"body-sm-bold"}>
                    {t("path_pick:basket.buyer_info.load_error.retry")}
                  </Text>
                </AppButton>
              }
            >
              {t("path_pick:basket.buyer_info.load_error.description")}
            </PathPickInlineNotice>
          )}

          <FormControl isInvalid={hasError}>
            <FormLabel
              htmlFor={"start-guide-delivery-template-code"}
              textStyle={"body-sm-bold"}
              color={"#22221F"}
              marginBottom={"6px"}
            >
              {t("path_pick_guide:delivery_template.code_label")}
            </FormLabel>
            <Input
              id={"start-guide-delivery-template-code"}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setHasError(false);
              }}
              size={"sm"}
              borderRadius={"4px"}
            />
            <FormErrorMessage>
              {t("path_pick_guide:delivery_template.error_empty")}
            </FormErrorMessage>
          </FormControl>

          <PathPickStartGuideOfficialGuideLink
            href={SMARTSTORE_DELIVERY_TEMPLATE_GUIDE_URL}
          />

          <Flex
            gap={"8px"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            paddingTop={"4px"}
          >
            <AppButton
              variant={"primary"}
              isDisabled={isSaved || isSmartStoreInfoUnavailable}
              isLoading={isSaving}
              onClick={handleSave}
            >
              {t(
                isSaved
                  ? "path_pick_guide:delivery_template.saved"
                  : "path_pick_guide:delivery_template.save"
              )}
            </AppButton>
            <AppButton variant={"normal"} onClick={onClose}>
              {t("path_pick_guide:close")}
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickStartGuideDeliveryTemplateModal;
